import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import type { FlowFrames, RunoutStats, TrajectoryPoint } from '../sim/avalanche/flow.ts';
import {
  cellCenterX,
  cellCenterY,
  gridHeight,
  gridWidth,
  sampleBilinear,
  type ScalarField,
} from '../sim/grid.ts';
import type { Conditions, SnowpackState, TerrainState } from '../sim/scene.ts';
import { Grain, topLayer } from '../sim/snowpack.ts';
import type { AvalancheProblem } from '../sim/stability.ts';
import type { OverlayMode } from '../state/store.ts';
import { AvalancheParticles, type ParticleColorMode } from './avalancheParticles.ts';
import { computeColors, windSpeedColor, windSpeedRampMax } from './overlayColors.ts';
import {
  makeHeightSampler,
  marchHeightfield,
  meshOrigin,
  meshToUtm,
  nearestCell,
  utmToMesh,
  type HoverSample,
  type MeshOrigin,
} from './probe.ts';

/**
 * Vanilla three.js terrain viewer. Geometry is built once per terrain and updated in place:
 * `update()` diffs the incoming props against the last call and does the minimal amount of
 * work — full geometry rebuild only when the terrain itself changes, position+normal+colour
 * rewrite when snow depth or vertical exaggeration change, colour-only rewrite when just the
 * overlay (or the conditions it's coloured from) changes.
 */

/** Per-cell avalanche data for the hover readout, plus the selected run's runout/trajectory for
 * the 3D markers — all on the terrain grid, in plain sim units. */
export interface AvalancheSceneData {
  /** m, cumulative deposit depth per terrain cell (all runs). */
  cumulativeDeposit: Float32Array | null;
  /** m, cumulative net snow-depth change per terrain cell (all runs). */
  net: Float32Array | null;
  /** The selected run's per-cell fields, for the origin/speed/pressure hover readout. */
  selectedRun: {
    deposit: Float32Array;
    depositEntrained: Float32Array;
    maxSpeed: Float32Array;
    maxPressureKPa: Float32Array;
  } | null;
  runout: RunoutStats | null;
  trajectory: readonly TrajectoryPoint[] | null;
}

/** Hover-preview release outline while the trigger tool is armed. */
export interface TriggerPreview {
  mode: 'brush' | 'natural';
  /** UTM. */
  point: { x: number; y: number };
  /** m, brush mode only. */
  radius: number;
  /** Terrain cell indices that would release. */
  cells: Int32Array;
}

export interface CrossSectionLineUtm {
  a: { x: number; y: number };
  b: { x: number; y: number };
}

/** A click waiting in the trigger queue, shown as a small numbered marker until it starts. */
export interface QueuedMarkerUtm {
  x: number;
  y: number;
  /** 1-based position in the queue. */
  order: number;
}

export interface SceneUpdateProps {
  terrain: TerrainState;
  /** Snow depth (m), already resampled onto the terrain grid. Zero when no snowpack result
   * exists yet, so the mesh always has bare terrain to fall back on. */
  snowDepthOnTerrain: ScalarField;
  /** Latest snowpack result, for hover readouts and wind arrows. Null before the first result. */
  snowpack: SnowpackState | null;
  overlay: OverlayMode;
  /** Data for the active storm/stability/avalanche overlay, already resampled onto the terrain
   * grid; null for the natural/slope overlays and while no (matching) result exists yet. */
  activeOverlayField: ScalarField | null;
  conditions: Conditions;
  exaggeration: number;
  showWindArrows: boolean;
  /** Snowpack-grid cell index marked in the scene (snow-profile selection), or null for none. */
  selectedCell: number | null;
  /** Avalanche hover data + selected run's runout markers, or null when no run exists. */
  avalanche: AvalancheSceneData | null;
}

const SKY_COLOR = 0x9fc4e0;

/**
 * Light intensities per view. Natural view is lit brightly so snow reads as white; data overlays
 * use softer light so their colours aren't blown out (colour = albedo × total light, clipped).
 */
const LIGHTING = {
  natural: { hemi: 1.5, sun: 1.9 },
  overlay: { hemi: 0.95, sun: 1.1 },
} as const;

export class SceneRenderer {
  private readonly container: HTMLElement;
  private readonly renderer: THREE.WebGLRenderer;
  private readonly scene: THREE.Scene;
  private readonly camera: THREE.PerspectiveCamera;
  private readonly controls: OrbitControls;
  private readonly material: THREE.MeshLambertMaterial;
  private readonly raycaster = new THREE.Raycaster();
  private readonly resizeObserver: ResizeObserver;
  private readonly hemiLight: THREE.HemisphereLight;
  private readonly sunLight: THREE.DirectionalLight;

  private mesh: THREE.Mesh | null = null;
  private geometry: THREE.BufferGeometry | null = null;
  private positionsArray: Float32Array | null = null;
  private colorsArray: Float32Array | null = null;
  private origin: MeshOrigin | null = null;
  private props: SceneUpdateProps | null = null;
  private windArrowMesh: THREE.InstancedMesh | null = null;
  private selectionMarker: THREE.Mesh | null = null;
  private readonly avalancheParticles: AvalancheParticles;
  private triggerPreview: TriggerPreview | null = null;
  private triggerPreviewGroup: THREE.Group | null = null;
  private crossSectionPending: { x: number; y: number } | null = null;
  private crossSectionLine: CrossSectionLineUtm | null = null;
  private crossSectionGroup: THREE.Group | null = null;
  private runoutGroup: THREE.Group | null = null;
  private queuedMarkers: readonly QueuedMarkerUtm[] = [];
  private queuedMarkersGroup: THREE.Group | null = null;
  /** Highest mesh vertex Y seen on the last position rewrite (elevation + snow, exaggerated) —
   * used to size the hover probe's bounding box, since snow can raise the surface well above
   * the bare-ground elevation range. */
  private maxSurfaceHeight = 0;

  private frameHandle = 0;
  private disposed = false;
  private pointerNDC: THREE.Vector2 | null = null;
  private pointerDirty = false;
  private hoverHandler: ((sample: HoverSample | null) => void) | null = null;
  private clickHandler: ((sample: HoverSample) => void) | null = null;
  /** Screen position of the last pointerdown, to tell a click from an orbit drag on pointerup. */
  private pointerDownAt: { x: number; y: number } | null = null;

  constructor(container: HTMLElement) {
    this.container = container;

    this.renderer = new THREE.WebGLRenderer({ antialias: true });
    this.renderer.setPixelRatio(Math.min(2, window.devicePixelRatio || 1));
    container.appendChild(this.renderer.domElement);

    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(SKY_COLOR);

    this.camera = new THREE.PerspectiveCamera(50, 1, 1, 20000);
    this.camera.position.set(0, 500, 1000);

    this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.08;
    this.controls.maxPolarAngle = Math.PI * 0.49;

    this.hemiLight = new THREE.HemisphereLight(0xeaf2ff, 0x4a4236, LIGHTING.natural.hemi);
    this.scene.add(this.hemiLight);
    this.sunLight = new THREE.DirectionalLight(0xfff4e6, LIGHTING.natural.sun);
    this.sunLight.position.set(-1, 1.1, 0.6);
    this.scene.add(this.sunLight);

    this.material = new THREE.MeshLambertMaterial({ vertexColors: true });
    this.avalancheParticles = new AvalancheParticles(this.scene);

    this.resizeObserver = new ResizeObserver(() => this.handleResize());
    this.resizeObserver.observe(container);
    this.handleResize();

    this.renderer.domElement.addEventListener('pointermove', this.handlePointerMove);
    this.renderer.domElement.addEventListener('pointerleave', this.handlePointerLeave);
    this.renderer.domElement.addEventListener('pointerdown', this.handlePointerDown);
    this.renderer.domElement.addEventListener('pointerup', this.handlePointerUp);

    this.frameHandle = requestAnimationFrame(this.animate);
  }

  setHoverHandler(cb: ((sample: HoverSample | null) => void) | null): void {
    this.hoverHandler = cb;
  }

  /** Called when the terrain is clicked (pointer down + up without dragging past a few pixels,
   * so it doesn't fire on orbit drags). */
  setClickHandler(cb: ((sample: HoverSample) => void) | null): void {
    this.clickHandler = cb;
  }

  /** Subscribe to camera changes (orbit/zoom/pan and programmatic re-fits). Returns an
   * unsubscribe function. */
  onCameraChange(cb: () => void): () => void {
    this.controls.addEventListener('change', cb);
    return () => this.controls.removeEventListener('change', cb);
  }

  /** Current camera azimuth around the vertical axis (radians), for a north-arrow indicator. */
  getAzimuthalAngle(): number {
    return this.controls.getAzimuthalAngle();
  }

  /** Approximate metres-per-pixel at the orbit target's depth, for a scale bar. */
  getScaleMetersPerPixel(): number {
    const distance = this.camera.position.distanceTo(this.controls.target);
    const vFov = THREE.MathUtils.degToRad(this.camera.fov);
    const worldHeight = 2 * Math.tan(vFov / 2) * distance;
    const pixelHeight = this.renderer.domElement.clientHeight || 1;
    return worldHeight / pixelHeight;
  }

  update(props: SceneUpdateProps): void {
    const prev = this.props;
    const terrainChanged = prev?.terrain !== props.terrain;
    const positionsChanged =
      terrainChanged ||
      prev?.snowDepthOnTerrain !== props.snowDepthOnTerrain ||
      prev?.exaggeration !== props.exaggeration;
    const colorsChanged =
      positionsChanged ||
      prev?.overlay !== props.overlay ||
      prev?.conditions !== props.conditions ||
      prev?.activeOverlayField !== props.activeOverlayField;
    const windChanged =
      terrainChanged ||
      prev?.snowpack !== props.snowpack ||
      prev?.exaggeration !== props.exaggeration ||
      prev?.showWindArrows !== props.showWindArrows;
    const runoutChanged =
      terrainChanged ||
      prev?.avalanche?.runout !== props.avalanche?.runout ||
      prev?.exaggeration !== props.exaggeration;
    this.props = props;

    if (terrainChanged) {
      this.buildGeometry(props.terrain);
      this.fitCamera(props.terrain, props.exaggeration);
    }
    if (positionsChanged)
      this.rewritePositions(props.terrain, props.snowDepthOnTerrain, props.exaggeration);
    if (colorsChanged)
      this.rewriteColors(
        props.terrain,
        props.snowDepthOnTerrain,
        props.overlay,
        props.conditions,
        props.activeOverlayField,
      );
    if (windChanged) this.rebuildWindArrows(props);
    if (runoutChanged) this.rebuildRunoutGroup(props.avalanche, props.exaggeration);
    this.updateSelectionMarker(props);
    const light = props.overlay === 'natural' ? LIGHTING.natural : LIGHTING.overlay;
    this.hemiLight.intensity = light.hemi;
    this.sunLight.intensity = light.sun;
  }

  /** Decode + upload one frame (interpolated) of the selected run's particle playback. Called
   * every animation frame while playing, independent of the main `update()` diffing above. */
  updateParticles(
    frames: FlowFrames | null,
    time: number,
    colorMode: ParticleColorMode,
    showDeposited: boolean,
    hide: boolean,
  ): void {
    const props = this.props;
    const origin = this.origin;
    if (hide || !frames || !props || !origin) {
      this.avalancheParticles.hide();
      return;
    }
    this.avalancheParticles.setViewport(
      this.renderer.domElement.clientHeight,
      this.camera.fov,
      this.renderer.getPixelRatio(),
    );
    this.avalancheParticles.update({
      frames,
      time,
      colorMode,
      showDeposited,
      origin,
      minElevation: props.terrain.minElevation,
      exaggeration: props.exaggeration,
    });
  }

  /** Hover preview while the trigger tool is armed: the brush circle (brush mode) and a lifted
   * highlight over the terrain cells that would release. Cheap enough to call every hover tick —
   * geometry buffers are reused across calls, only the used prefix is rewritten. */
  setTriggerPreview(preview: TriggerPreview | null): void {
    this.triggerPreview = preview;
    this.rebuildTriggerPreview();
  }

  /** Cross-section tool: the first clicked point (while waiting for the second) and/or the
   * completed line, both in UTM. */
  setCrossSection(
    pending: { x: number; y: number } | null,
    line: CrossSectionLineUtm | null,
  ): void {
    this.crossSectionPending = pending;
    this.crossSectionLine = line;
    this.rebuildCrossSection();
  }

  /** Small numbered markers at the queued click points (both modes), removed as each starts. */
  setQueuedMarkers(markers: readonly QueuedMarkerUtm[]): void {
    this.queuedMarkers = markers;
    this.rebuildQueuedMarkers();
  }

  dispose(): void {
    this.disposed = true;
    cancelAnimationFrame(this.frameHandle);
    this.resizeObserver.disconnect();
    this.renderer.domElement.removeEventListener('pointermove', this.handlePointerMove);
    this.renderer.domElement.removeEventListener('pointerleave', this.handlePointerLeave);
    this.renderer.domElement.removeEventListener('pointerdown', this.handlePointerDown);
    this.renderer.domElement.removeEventListener('pointerup', this.handlePointerUp);
    this.controls.dispose();
    this.disposeWindArrows();
    this.disposeSelectionMarker();
    this.disposeTriggerPreview();
    this.disposeCrossSection();
    this.disposeRunoutGroup();
    this.disposeQueuedMarkers();
    this.avalancheParticles.dispose();
    this.geometry?.dispose();
    this.material.dispose();
    this.renderer.dispose();
    this.renderer.domElement.remove();
  }

  private buildGeometry(terrain: TerrainState): void {
    const spec = terrain.plan.terrain;
    const cols = spec.cols;
    const rows = spec.rows;
    const count = cols * rows;
    const origin = meshOrigin(spec);
    this.origin = origin;

    const positions = new Float32Array(count * 3);
    for (let r = 0; r < rows; r++) {
      const zMesh = -(cellCenterY(spec, r) - origin.cy);
      for (let c = 0; c < cols; c++) {
        const idx = r * cols + c;
        positions[idx * 3] = cellCenterX(spec, c) - origin.cx;
        positions[idx * 3 + 2] = zMesh;
      }
    }

    const quadsX = Math.max(0, cols - 1);
    const quadsY = Math.max(0, rows - 1);
    const indices = new Uint32Array(quadsX * quadsY * 6);
    let ii = 0;
    for (let r = 0; r < quadsY; r++) {
      for (let c = 0; c < quadsX; c++) {
        const a = r * cols + c;
        const b = a + 1;
        const c0 = a + cols;
        const d = c0 + 1;
        indices[ii++] = a;
        indices[ii++] = c0;
        indices[ii++] = b;
        indices[ii++] = b;
        indices[ii++] = c0;
        indices[ii++] = d;
      }
    }

    const colors = new Float32Array(count * 3);

    const geometry = new THREE.BufferGeometry();
    geometry.setIndex(new THREE.BufferAttribute(indices, 1));
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));

    this.positionsArray = positions;
    this.colorsArray = colors;
    this.geometry?.dispose();
    this.geometry = geometry;

    if (this.mesh) {
      this.mesh.geometry = geometry;
    } else {
      this.mesh = new THREE.Mesh(geometry, this.material);
      this.scene.add(this.mesh);
    }
  }

  private rewritePositions(
    terrain: TerrainState,
    snowDepthOnTerrain: ScalarField,
    exaggeration: number,
  ): void {
    const positions = this.positionsArray;
    const geometry = this.geometry;
    if (!positions || !geometry) return;
    const elev = terrain.elevation.data;
    const snow = snowDepthOnTerrain.data;
    const minEl = terrain.minElevation;
    let maxHeight = 0;
    for (let i = 0; i < elev.length; i++) {
      const h = (elev[i]! - minEl + Math.max(0, snow[i]!)) * exaggeration;
      positions[i * 3 + 1] = h;
      if (h > maxHeight) maxHeight = h;
    }
    this.maxSurfaceHeight = maxHeight;
    geometry.getAttribute('position').needsUpdate = true;
    geometry.computeVertexNormals();
    geometry.computeBoundingSphere();
  }

  private rewriteColors(
    terrain: TerrainState,
    snowDepthOnTerrain: ScalarField,
    overlay: OverlayMode,
    conditions: Conditions,
    activeOverlayField: ScalarField | null,
  ): void {
    const colors = this.colorsArray;
    const geometry = this.geometry;
    if (!colors || !geometry) return;
    computeColors(colors, terrain, snowDepthOnTerrain, overlay, conditions, activeOverlayField);
    geometry.getAttribute('color').needsUpdate = true;
  }

  private disposeWindArrows(): void {
    if (!this.windArrowMesh) return;
    this.scene.remove(this.windArrowMesh);
    this.windArrowMesh.geometry.dispose();
    (this.windArrowMesh.material as THREE.Material).dispose();
    this.windArrowMesh = null;
  }

  /** Instanced downwind-pointing arrows on a coarse subgrid of the snowpack grid, coloured by
   * local wind speed. Rebuilt whenever the snowpack result, exaggeration or the toggle changes. */
  private rebuildWindArrows(props: SceneUpdateProps): void {
    this.disposeWindArrows();
    const origin = this.origin;
    const wind = props.snowpack?.storm?.wind;
    if (!props.showWindArrows || !wind || !origin) return;

    const spec = wind.speed.spec;
    const areaSize = Math.max(gridWidth(spec), gridHeight(spec));
    const spacingM = Math.max(2 * spec.cellSize, areaSize / 22);
    const stepCells = Math.max(1, Math.round(spacingM / spec.cellSize));
    const maxSpeed = windSpeedRampMax(wind.speed);
    const baseLength = spacingM * 0.6;
    const baseRadius = spacingM * 0.12;
    const heightOffset = spacingM * 0.15;

    const elevation = props.terrain.elevation;
    const snowDepth = props.snowDepthOnTerrain;
    const minEl = props.terrain.minElevation;
    const exaggeration = props.exaggeration;
    const DEG = Math.PI / 180;

    interface ArrowInstance {
      x: number;
      y: number;
      z: number;
      angle: number;
      length: number;
      radius: number;
      color: readonly [number, number, number];
    }
    const instances: ArrowInstance[] = [];
    const startR = Math.floor(stepCells / 2);
    const startC = Math.floor(stepCells / 2);
    for (let r = startR; r < spec.rows; r += stepCells) {
      for (let c = startC; c < spec.cols; c += stepCells) {
        const idx = r * spec.cols + c;
        const speed = wind.speed.data[idx]!;
        if (speed < 0.1) continue;
        const xUtm = cellCenterX(spec, c);
        const yUtm = cellCenterY(spec, r);
        const elev = sampleBilinear(elevation, xUtm, yUtm);
        const snow = sampleBilinear(snowDepth, xUtm, yUtm);
        const h = (elev - minEl + Math.max(0, snow)) * exaggeration + heightOffset;
        const [xMesh, zMesh] = utmToMesh(origin, xUtm, yUtm);
        const rad = wind.fromGridDeg.data[idx]! * DEG;
        // Wind blows towards fromGrid + 180°; mesh (x, z) = (−sin b, +cos b).
        const dirX = -Math.sin(rad);
        const dirZ = Math.cos(rad);
        const angle = Math.atan2(dirX, dirZ);
        const factor = 0.4 + Math.min(1.6, speed / 15);
        instances.push({
          x: xMesh,
          y: h,
          z: zMesh,
          angle,
          length: baseLength * factor,
          radius: baseRadius * Math.sqrt(factor),
          color: windSpeedColor(speed, maxSpeed),
        });
      }
    }
    if (instances.length === 0) return;

    // Cone pointing along +Z, spanning [0, 1] in Z (base at the placement point).
    const geometry = new THREE.ConeGeometry(1, 1, 8);
    geometry.rotateX(Math.PI / 2);
    geometry.translate(0, 0, 0.5);
    // Per-instance colour comes from setColorAt; `vertexColors` would multiply it by the cone's
    // (absent → black) vertex colours.
    const material = new THREE.MeshBasicMaterial();
    const mesh = new THREE.InstancedMesh(geometry, material, instances.length);
    const matrix = new THREE.Matrix4();
    const quaternion = new THREE.Quaternion();
    const position = new THREE.Vector3();
    const scale = new THREE.Vector3();
    const axisY = new THREE.Vector3(0, 1, 0);
    const color = new THREE.Color();
    instances.forEach((inst, i) => {
      position.set(inst.x, inst.y, inst.z);
      quaternion.setFromAxisAngle(axisY, inst.angle);
      scale.set(inst.radius, inst.radius, inst.length);
      matrix.compose(position, quaternion, scale);
      mesh.setMatrixAt(i, matrix);
      color.setRGB(inst.color[0], inst.color[1], inst.color[2]);
      mesh.setColorAt(i, color);
    });
    mesh.instanceMatrix.needsUpdate = true;
    if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
    this.scene.add(mesh);
    this.windArrowMesh = mesh;
  }

  private fitCamera(terrain: TerrainState, exaggeration: number): void {
    const spec = terrain.plan.terrain;
    const width = gridWidth(spec);
    const depth = gridHeight(spec);
    const heightSpan = Math.max(1, (terrain.maxElevation - terrain.minElevation) * exaggeration);
    const maxDim = Math.max(width, depth, 10);
    const targetY = heightSpan * 0.4;
    const distance = maxDim * 1.1;

    this.camera.near = Math.max(0.5, maxDim / 2000);
    this.camera.far = Math.max(5000, maxDim * 4);
    this.camera.position.set(0, targetY + distance * 0.55, distance * 0.85);
    this.camera.updateProjectionMatrix();

    this.controls.target.set(0, targetY, 0);
    this.controls.minDistance = Math.max(5, maxDim * 0.02);
    this.controls.maxDistance = maxDim * 5;
    this.controls.update();

    // The initial camera sits ~1.1–1.6 × maxDim from the terrain; fog must start beyond the
    // far edge of the area or it swallows the scene. It only fades things when zoomed far out.
    this.scene.fog = new THREE.Fog(SKY_COLOR, maxDim * 2.5, maxDim * 6);
  }

  private handleResize(): void {
    const width = this.container.clientWidth || 1;
    const height = this.container.clientHeight || 1;
    this.renderer.setSize(width, height, false);
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
  }

  private readonly animate = (): void => {
    if (this.disposed) return;
    this.frameHandle = requestAnimationFrame(this.animate);
    this.controls.update();
    if (this.pointerDirty) {
      this.pointerDirty = false;
      this.processHover();
    }
    this.renderer.render(this.scene, this.camera);
  };

  private readonly handlePointerMove = (ev: PointerEvent): void => {
    const rect = this.renderer.domElement.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return;
    const x = ((ev.clientX - rect.left) / rect.width) * 2 - 1;
    const y = -((ev.clientY - rect.top) / rect.height) * 2 + 1;
    this.pointerNDC = new THREE.Vector2(x, y);
    this.pointerDirty = true;
  };

  private readonly handlePointerLeave = (): void => {
    this.pointerNDC = null;
    this.pointerDirty = false;
    this.hoverHandler?.(null);
  };

  private readonly handlePointerDown = (ev: PointerEvent): void => {
    this.pointerDownAt = { x: ev.clientX, y: ev.clientY };
  };

  /** A plain click (down + up within a few pixels) selects the cell under the cursor; a drag
   * (orbiting the camera) must not. */
  private readonly handlePointerUp = (ev: PointerEvent): void => {
    const down = this.pointerDownAt;
    this.pointerDownAt = null;
    if (!down || !this.clickHandler) return;
    if (Math.hypot(ev.clientX - down.x, ev.clientY - down.y) > 4) return;
    const rect = this.renderer.domElement.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return;
    const x = ((ev.clientX - rect.left) / rect.width) * 2 - 1;
    const y = -((ev.clientY - rect.top) / rect.height) * 2 + 1;
    const sample = this.sampleAt(new THREE.Vector2(x, y));
    if (sample) this.clickHandler(sample);
  };

  private processHover(): void {
    const pointerNDC = this.pointerNDC;
    const sample = pointerNDC ? this.sampleAt(pointerNDC) : null;
    this.hoverHandler?.(sample);
  }

  private sampleAt(pointerNDC: THREE.Vector2): HoverSample | null {
    const props = this.props;
    const origin = this.origin;
    if (!props || !origin) return null;

    this.raycaster.setFromCamera(pointerNDC, this.camera);
    const spec = props.terrain.plan.terrain;
    const width = gridWidth(spec);
    const depth = gridHeight(spec);
    const box = {
      min: { x: -width / 2, y: -1, z: -depth / 2 },
      max: { x: width / 2, y: this.maxSurfaceHeight + 1, z: depth / 2 },
    };
    const heightAt = makeHeightSampler(
      props.terrain.elevation,
      props.snowDepthOnTerrain,
      props.terrain.minElevation,
      props.exaggeration,
      origin,
    );
    const ray = {
      origin: {
        x: this.raycaster.ray.origin.x,
        y: this.raycaster.ray.origin.y,
        z: this.raycaster.ray.origin.z,
      },
      dir: {
        x: this.raycaster.ray.direction.x,
        y: this.raycaster.ray.direction.y,
        z: this.raycaster.ray.direction.z,
      },
    };
    const hit = marchHeightfield(ray, heightAt, box, Math.max(0.5, spec.cellSize / 2));
    if (!hit) return null;

    const [xUtm, yUtm] = meshToUtm(origin, hit.x, hit.z);
    const { col, row } = nearestCell(spec, xUtm, yUtm);
    const idx = row * spec.cols + col;
    const sample: HoverSample = {
      xUtm,
      yUtm,
      elevation: props.terrain.elevation.data[idx]!,
      slope: props.terrain.topo.slope.data[idx]!,
      aspect: props.terrain.topo.aspect.data[idx]!,
      snowDepth: props.snowDepthOnTerrain.data[idx]!,
    };

    const sp = props.snowpack;
    if (sp) {
      const { col: sc, row: sr } = nearestCell(sp.spec, xUtm, yUtm);
      const sidx = sr * sp.spec.cols + sc;
      sample.snowpackCell = sidx;
      sample.layerCount = sp.columns.count[sidx]!;
      const top = topLayer(sp.columns, sidx);
      if (top >= 0) sample.topGrain = sp.columns.grain[top]! as Grain;
      sample.skier = sp.stability.skier.data[sidx]!;
      sample.problem = sp.stability.problem[sidx]! as AvalancheProblem;
      const criticalGrain = sp.stability.criticalGrain[sidx]!;
      if (criticalGrain !== 255) {
        sample.criticalGrain = criticalGrain as Grain;
        sample.criticalDepth = sp.stability.criticalDepth.data[sidx]!;
      }
      if (sp.storm) {
        sample.stormSnowfall = sampleBilinear(sp.storm.snowfall, xUtm, yUtm);
        sample.windChange = sampleBilinear(sp.storm.windChange, xUtm, yUtm);
        sample.rain = sampleBilinear(sp.storm.rain, xUtm, yUtm);
        if (sp.storm.wind) {
          sample.windSpeed = sampleBilinear(sp.storm.wind.speed, xUtm, yUtm);
          sample.windFromGridDeg = sampleBilinear(sp.storm.wind.fromGridDeg, xUtm, yUtm);
          sample.sx = sampleBilinear(sp.storm.wind.sx, xUtm, yUtm);
        }
      }
    }

    const av = props.avalanche;
    if (av) {
      const debris = av.cumulativeDeposit?.[idx] ?? 0;
      const net = av.net?.[idx] ?? 0;
      if (debris > 0.001 || Math.abs(net) > 0.001) {
        sample.avDebrisDepth = debris;
        sample.avRemovedDepth = Math.max(0, debris - net);
        sample.avNetChange = net;
      }
      if (av.selectedRun) {
        const dep = av.selectedRun.deposit[idx]!;
        if (dep > 0) sample.avOriginSharePct = (av.selectedRun.depositEntrained[idx]! / dep) * 100;
        const speed = av.selectedRun.maxSpeed[idx]!;
        const pressure = av.selectedRun.maxPressureKPa[idx]!;
        if (speed > 0) sample.avMaxSpeed = speed;
        if (pressure > 0) sample.avMaxPressureKPa = pressure;
      }
    }
    return sample;
  }

  /** Small ring marking the snowpack cell selected for the snow-profile panel, or none removed
   * when nothing is selected. */
  private updateSelectionMarker(props: SceneUpdateProps): void {
    const origin = this.origin;
    const sp = props.snowpack;
    const cell = props.selectedCell;
    if (cell === null || !sp || !origin || cell < 0 || cell >= sp.columns.count.length) {
      this.disposeSelectionMarker();
      return;
    }
    const spec = sp.spec;
    const row = Math.floor(cell / spec.cols);
    const col = cell % spec.cols;
    const xUtm = cellCenterX(spec, col);
    const yUtm = cellCenterY(spec, row);
    const [xMesh, zMesh] = utmToMesh(origin, xUtm, yUtm);
    const heightAt = makeHeightSampler(
      props.terrain.elevation,
      props.snowDepthOnTerrain,
      props.terrain.minElevation,
      props.exaggeration,
      origin,
    );
    const y = heightAt(xMesh, zMesh) + Math.max(0.3, spec.cellSize * 0.05);

    if (!this.selectionMarker) {
      const geometry = new THREE.RingGeometry(0.7, 1, 32);
      geometry.rotateX(-Math.PI / 2);
      const material = new THREE.MeshBasicMaterial({
        color: 0xff3030,
        side: THREE.DoubleSide,
        depthTest: false,
        transparent: true,
        opacity: 0.9,
      });
      this.selectionMarker = new THREE.Mesh(geometry, material);
      this.selectionMarker.renderOrder = 999;
      this.scene.add(this.selectionMarker);
    }
    const scale = Math.max(1, spec.cellSize * 0.7);
    this.selectionMarker.scale.set(scale, 1, scale);
    this.selectionMarker.position.set(xMesh, y, zMesh);
  }

  private disposeSelectionMarker(): void {
    if (!this.selectionMarker) return;
    this.scene.remove(this.selectionMarker);
    this.selectionMarker.geometry.dispose();
    (this.selectionMarker.material as THREE.Material).dispose();
    this.selectionMarker = null;
  }

  /** Height sampler (mesh-space) matching the current terrain + snow surface, or null before the
   * first `update()`. Shared by the trigger preview, cross-section and runout marker builders. */
  private currentHeightSampler(): ((xMesh: number, zMesh: number) => number) | null {
    const props = this.props;
    const origin = this.origin;
    if (!props || !origin) return null;
    return makeHeightSampler(
      props.terrain.elevation,
      props.snowDepthOnTerrain,
      props.terrain.minElevation,
      props.exaggeration,
      origin,
    );
  }

  private static disposeGroup(group: THREE.Group): void {
    group.traverse((obj) => {
      if (obj instanceof THREE.Mesh || obj instanceof THREE.Line || obj instanceof THREE.Points) {
        obj.geometry.dispose();
        const m = obj.material;
        if (Array.isArray(m)) m.forEach((mm) => mm.dispose());
        else m.dispose();
      }
    });
  }

  private disposeTriggerPreview(): void {
    if (!this.triggerPreviewGroup) return;
    this.scene.remove(this.triggerPreviewGroup);
    SceneRenderer.disposeGroup(this.triggerPreviewGroup);
    this.triggerPreviewGroup = null;
  }

  private rebuildTriggerPreview(): void {
    this.disposeTriggerPreview();
    const preview = this.triggerPreview;
    const origin = this.origin;
    const heightAt = this.currentHeightSampler();
    const props = this.props;
    if (!preview || !origin || !heightAt || !props) return;
    const spec = props.terrain.plan.terrain;
    const lift = Math.max(0.3, spec.cellSize * 0.15);
    const group = new THREE.Group();

    if (preview.mode === 'brush' && preview.radius > 0) {
      const SEGMENTS = 64;
      const positions = new Float32Array((SEGMENTS + 1) * 3);
      for (let k = 0; k <= SEGMENTS; k++) {
        const a = (k / SEGMENTS) * Math.PI * 2;
        const x = preview.point.x + Math.cos(a) * preview.radius;
        const y = preview.point.y + Math.sin(a) * preview.radius;
        const [mx, mz] = utmToMesh(origin, x, y);
        positions[k * 3] = mx;
        positions[k * 3 + 1] = heightAt(mx, mz) + lift;
        positions[k * 3 + 2] = mz;
      }
      const geom = new THREE.BufferGeometry();
      geom.setAttribute('position', new THREE.BufferAttribute(positions, 3));
      const mat = new THREE.LineBasicMaterial({
        color: 0xffa500,
        transparent: true,
        opacity: 0.9,
        depthTest: false,
      });
      const line = new THREE.Line(geom, mat);
      line.renderOrder = 10;
      group.add(line);
    }

    const cells = preview.cells;
    if (cells.length > 0) {
      const MAX_POINTS = 40_000;
      const stride = Math.max(1, Math.ceil(cells.length / MAX_POINTS));
      const count = Math.ceil(cells.length / stride);
      const positions = new Float32Array(count * 3);
      let w = 0;
      for (let k = 0; k < cells.length; k += stride) {
        const i = cells[k]!;
        const c = i % spec.cols;
        const r = Math.floor(i / spec.cols);
        const xUtm = cellCenterX(spec, c);
        const yUtm = cellCenterY(spec, r);
        const [mx, mz] = utmToMesh(origin, xUtm, yUtm);
        positions[w * 3] = mx;
        positions[w * 3 + 1] = heightAt(mx, mz) + lift * 0.6;
        positions[w * 3 + 2] = mz;
        w++;
      }
      const geom = new THREE.BufferGeometry();
      geom.setAttribute('position', new THREE.BufferAttribute(positions.subarray(0, w * 3), 3));
      // Fixed pixel size: with world-size points (a few metres) the preview shrank to ~1 px
      // from the default camera distance and disappeared. Cyan clashes with none of the overlay
      // ramps (the stability overlay is red/orange/green).
      const mat = new THREE.PointsMaterial({
        color: 0x00e5ff,
        size: 4,
        sizeAttenuation: false,
        transparent: true,
        opacity: 0.85,
        depthTest: false,
      });
      const points = new THREE.Points(geom, mat);
      points.renderOrder = 9;
      group.add(points);
    }

    if (group.children.length === 0) return;
    group.renderOrder = 9;
    this.scene.add(group);
    this.triggerPreviewGroup = group;
  }

  private disposeCrossSection(): void {
    if (!this.crossSectionGroup) return;
    this.scene.remove(this.crossSectionGroup);
    SceneRenderer.disposeGroup(this.crossSectionGroup);
    this.crossSectionGroup = null;
  }

  private rebuildCrossSection(): void {
    this.disposeCrossSection();
    const origin = this.origin;
    const heightAt = this.currentHeightSampler();
    const props = this.props;
    if (!origin || !heightAt || !props) return;
    const group = new THREE.Group();
    const lift = 0.5;
    const markerRadius = Math.max(1, props.terrain.plan.terrain.cellSize * 0.6);

    const addMarker = (x: number, y: number, color: number): void => {
      const [mx, mz] = utmToMesh(origin, x, y);
      const geom = new THREE.SphereGeometry(markerRadius, 12, 8);
      const mat = new THREE.MeshBasicMaterial({ color, depthTest: false });
      const mesh = new THREE.Mesh(geom, mat);
      mesh.position.set(mx, heightAt(mx, mz) + lift, mz);
      mesh.renderOrder = 10;
      group.add(mesh);
    };

    if (this.crossSectionPending) {
      addMarker(this.crossSectionPending.x, this.crossSectionPending.y, 0xffd23f);
    }
    const line = this.crossSectionLine;
    if (line) {
      addMarker(line.a.x, line.a.y, 0xffd23f);
      addMarker(line.b.x, line.b.y, 0xffd23f);
      const SEGMENTS = 64;
      const positions = new Float32Array((SEGMENTS + 1) * 3);
      for (let k = 0; k <= SEGMENTS; k++) {
        const f = k / SEGMENTS;
        const x = line.a.x + (line.b.x - line.a.x) * f;
        const y = line.a.y + (line.b.y - line.a.y) * f;
        const [mx, mz] = utmToMesh(origin, x, y);
        positions[k * 3] = mx;
        positions[k * 3 + 1] = heightAt(mx, mz) + lift;
        positions[k * 3 + 2] = mz;
      }
      const geom = new THREE.BufferGeometry();
      geom.setAttribute('position', new THREE.BufferAttribute(positions, 3));
      const mat = new THREE.LineBasicMaterial({
        color: 0xffd23f,
        transparent: true,
        opacity: 0.95,
        depthTest: false,
      });
      const lineObj = new THREE.Line(geom, mat);
      lineObj.renderOrder = 10;
      group.add(lineObj);
    }

    if (group.children.length === 0) return;
    this.scene.add(group);
    this.crossSectionGroup = group;
  }

  /** Small filled circle with a number, for the queued-click markers. */
  private static makeNumberSprite(n: number): THREE.Sprite {
    const canvas = document.createElement('canvas');
    canvas.width = 64;
    canvas.height = 64;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.fillStyle = 'rgba(255, 138, 0, 0.95)';
      ctx.beginPath();
      ctx.arc(32, 32, 27, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 3;
      ctx.stroke();
      ctx.fillStyle = '#ffffff';
      ctx.font = 'bold 30px sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(String(n), 32, 33);
    }
    const texture = new THREE.CanvasTexture(canvas);
    const material = new THREE.SpriteMaterial({
      map: texture,
      depthTest: false,
      transparent: true,
    });
    const sprite = new THREE.Sprite(material);
    sprite.renderOrder = 11;
    return sprite;
  }

  private disposeQueuedMarkers(): void {
    if (!this.queuedMarkersGroup) return;
    this.scene.remove(this.queuedMarkersGroup);
    this.queuedMarkersGroup.traverse((obj) => {
      if (obj instanceof THREE.Sprite) {
        const material = obj.material as THREE.SpriteMaterial;
        material.map?.dispose();
        material.dispose();
      }
    });
    this.queuedMarkersGroup = null;
  }

  private rebuildQueuedMarkers(): void {
    this.disposeQueuedMarkers();
    const origin = this.origin;
    const heightAt = this.currentHeightSampler();
    const props = this.props;
    if (!origin || !heightAt || !props || this.queuedMarkers.length === 0) return;
    const spec = props.terrain.plan.terrain;
    const lift = Math.max(1, spec.cellSize * 0.6);
    const spriteSize = Math.max(3, spec.cellSize * 1.5);
    const group = new THREE.Group();
    for (const m of this.queuedMarkers) {
      const [mx, mz] = utmToMesh(origin, m.x, m.y);
      const sprite = SceneRenderer.makeNumberSprite(m.order);
      sprite.scale.set(spriteSize, spriteSize, 1);
      sprite.position.set(mx, heightAt(mx, mz) + lift, mz);
      group.add(sprite);
    }
    this.scene.add(group);
    this.queuedMarkersGroup = group;
  }

  private disposeRunoutGroup(): void {
    if (!this.runoutGroup) return;
    this.scene.remove(this.runoutGroup);
    SceneRenderer.disposeGroup(this.runoutGroup);
    this.runoutGroup = null;
  }

  /** Release-top and runout-tip markers, plus the centre-of-mass trajectory, for the selected
   * run — in flow coordinates (e = m east of the terrain grid's west edge, s = m south of its
   * north edge), converted to UTM then mesh space. */
  private rebuildRunoutGroup(avalanche: AvalancheSceneData | null, exaggeration: number): void {
    this.disposeRunoutGroup();
    const origin = this.origin;
    const props = this.props;
    if (!avalanche || !origin || !props) return;
    const spec = props.terrain.plan.terrain;
    const minEl = props.terrain.minElevation;
    const toMeshY = (z: number): number => (z - minEl) * exaggeration;
    const group = new THREE.Group();

    const addMarker = (e: number, s: number, z: number, color: number): void => {
      const xUtm = spec.minX + e;
      const yUtm = spec.maxY - s;
      const [mx, mz] = utmToMesh(origin, xUtm, yUtm);
      const geom = new THREE.SphereGeometry(Math.max(1.5, spec.cellSize * 0.8), 12, 8);
      const mat = new THREE.MeshBasicMaterial({ color, depthTest: false });
      const mesh = new THREE.Mesh(geom, mat);
      mesh.position.set(mx, toMeshY(z) + 1, mz);
      mesh.renderOrder = 8;
      group.add(mesh);
    };

    const runout = avalanche.runout;
    if (runout) {
      addMarker(runout.top.e, runout.top.s, runout.top.z, 0x40c4ff);
      addMarker(runout.tip.e, runout.tip.s, runout.tip.z, 0xff3b30);
    }

    const trajectory = avalanche.trajectory;
    if (trajectory && trajectory.length > 1) {
      const positions = new Float32Array(trajectory.length * 3);
      trajectory.forEach((p, k) => {
        const xUtm = spec.minX + p.e;
        const yUtm = spec.maxY - p.s;
        const [mx, mz] = utmToMesh(origin, xUtm, yUtm);
        positions[k * 3] = mx;
        positions[k * 3 + 1] = toMeshY(p.z) + 0.5;
        positions[k * 3 + 2] = mz;
      });
      const geom = new THREE.BufferGeometry();
      geom.setAttribute('position', new THREE.BufferAttribute(positions, 3));
      const mat = new THREE.LineBasicMaterial({
        color: 0xffffff,
        transparent: true,
        opacity: 0.7,
        depthTest: false,
      });
      const line = new THREE.Line(geom, mat);
      line.renderOrder = 8;
      group.add(line);
    }

    if (group.children.length === 0) return;
    this.scene.add(group);
    this.runoutGroup = group;
  }
}
